import { useState, useRef, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { 
  Upload, 
  FileText, 
  Loader2, 
  Check, 
  ChevronRight,
  Plus,
  FilePlus,
  X
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export interface InsertPagesData {
  cacheKey: string;
  selectedPages: number[];
  insertionPosition: string;
  versionId: string;
  showNewBadge: boolean;
}

interface PageUploadDialogProps {
  open: boolean;
  onClose: () => void;
  documentId: string;
  versionId: string;
  versionNumber: number;
  currentPages: any[];
  documentColor?: string;
  initialFile?: File | null;
  preloadedData?: any;
  entryMode?: 'choice' | 'addPages';
  onPagesInserted?: () => void;
  onProcessingComplete?: (pageIds: string[]) => void;
  documentCurrentVersion?: number;
}

type Step = 'choice' | 'upload' | 'select' | 'processing';

interface ThumbnailPage {
  pageNumber: number;
  thumbnailBase64: string;
  width: number;
  height: number;
}

export function PageUploadDialog({ 
  open, 
  onClose, 
  documentId, 
  versionId,
  versionNumber,
  currentPages,
  documentColor = "#ef4444",
  initialFile = null,
  preloadedData = null,
  entryMode = 'choice',
  onPagesInserted,
  onProcessingComplete,
  documentCurrentVersion
}: PageUploadDialogProps) {
  const [step, setStep] = useState<Step>(
    preloadedData ? 'select' : (entryMode === 'addPages' ? 'upload' : 'choice')
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [thumbnails, setThumbnails] = useState<ThumbnailPage[]>(preloadedData?.thumbnails || []);
  const [cacheKey, setCacheKey] = useState<string>(preloadedData?.cacheKey || '');
  const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingStatus, setProcessingStatus] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Clean up inline hover styles when dialog closes or unmounts
  useEffect(() => {
    if (!open) {
      // Clear any inline style overrides from hover states
      document.querySelectorAll('[data-testid^="page-thumbnail-"]').forEach((el) => {
        (el as HTMLElement).style.borderColor = '';
      });
      document.querySelectorAll('[data-testid^="position-"]').forEach((el) => {
        (el as HTMLElement).style.borderColor = '';
      });
    }
  }, [open]);

  // Helper to convert various color formats to rgba with alpha
  const colorToRgba = (color: string, alpha: number): string => {
    // For any color format that we can't easily parse to RGB, use color-mix
    // This handles CSS variables, HSL, HWB, and other modern color formats
    if (color.startsWith('var(') || color.startsWith('hsl') || color.startsWith('hwb')) {
      return `color-mix(in srgb, ${color} ${alpha * 100}%, transparent)`;
    }
    
    // Handle hex colors (3 or 6 digits)
    if (color.startsWith('#')) {
      const hex = color.slice(1);
      let r: number, g: number, b: number;
      
      if (hex.length === 3) {
        r = parseInt(hex[0] + hex[0], 16);
        g = parseInt(hex[1] + hex[1], 16);
        b = parseInt(hex[2] + hex[2], 16);
      } else if (hex.length === 6) {
        r = parseInt(hex.slice(0, 2), 16);
        g = parseInt(hex.slice(2, 4), 16);
        b = parseInt(hex.slice(4, 6), 16);
      } else {
        return `rgba(239, 68, 68, ${alpha})`; // fallback to red
      }
      
      if (isNaN(r) || isNaN(g) || isNaN(b)) {
        return `rgba(239, 68, 68, ${alpha})`; // fallback to red
      }
      
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    
    // Handle rgb/rgba
    if (color.startsWith('rgb')) {
      const match = color.match(/\d+/g);
      if (match && match.length >= 3) {
        return `rgba(${match[0]}, ${match[1]}, ${match[2]}, ${alpha})`;
      }
    }
    
    // Fallback: use color-mix for any unrecognized format
    return `color-mix(in srgb, ${color} ${alpha * 100}%, transparent)`;
  };

  const uploadThumbnailMutation = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('pdf', file);
      const response = await apiRequest('POST', `/api/documents/${documentId}/pages/upload`, formData);
      return response.json();
    },
    onSuccess: (data) => {
      setThumbnails(data.thumbnails);
      setCacheKey(data.cacheKey);
      setStep('select');
    },
    onError: () => {
      toast({
        title: "Upload failed",
        description: "Failed to process PDF. Please try again.",
        variant: "destructive"
      });
    }
  });


  // Update state when preloaded data changes
  useEffect(() => {
    if (preloadedData) {
      setThumbnails(preloadedData.thumbnails || []);
      setCacheKey(preloadedData.cacheKey || '');
      setStep('select');
    }
  }, [preloadedData]);

  // Only auto-upload if initialFile is provided (drag/drop scenario)
  useEffect(() => {
    if (preloadedData) {
      // Data is already loaded, skip upload step
      return;
    }
    
    if (open && !selectedFile && initialFile) {
      handleFileSelect(initialFile);
    }
  }, [open, initialFile, preloadedData]);


  const getStatusText = (status: string, progress: number, currentPage?: number, pageCount?: number) => {
    if (status === "processing" && currentPage && pageCount) {
      return `Converting page ${currentPage} of ${pageCount}...`;
    }
    switch (status) {
      case "pending":
        return "Preparing to process...";
      case "processing":
        return "Processing pages...";
      case "completed":
        return "Processing complete!";
      case "failed":
        return "Processing failed";
      default:
        return "Processing...";
    }
  };

  const handleClose = () => {
    // Reset to appropriate step based on whether we have preloaded data
    if (preloadedData) {
      setStep('select');
    } else {
      setStep(entryMode === 'addPages' ? 'upload' : 'choice');
    }
    
    setSelectedFile(null);
    // Don't clear thumbnails/cacheKey/selection if we have preloaded data
    if (!preloadedData) {
      setThumbnails([]);
      setCacheKey('');
      setSelectedPages(new Set());
    }
    setUploadProgress(0);
    setProcessingStatus('');
    onClose();
  };

  const handleFileSelect = (file: File) => {
    if (file.type !== 'application/pdf') {
      toast({
        title: "Invalid file type",
        description: "Please select a PDF file.",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 50 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Maximum file size is 50MB.",
        variant: "destructive"
      });
      return;
    }

    setSelectedFile(file);
    setStep('upload');
    uploadThumbnailMutation.mutate(file);
  };

  const togglePageSelection = (pageNumber: number) => {
    const newSelection = new Set(selectedPages);
    if (newSelection.has(pageNumber)) {
      newSelection.delete(pageNumber);
    } else {
      newSelection.add(pageNumber);
    }
    setSelectedPages(newSelection);
  };

  const handleConfirm = async () => {
    if (selectedPages.size === 0) {
      toast({
        title: "No pages selected",
        description: "Please select at least one page to insert.",
        variant: "destructive"
      });
      return;
    }

    try {
      setStep('processing');
      setUploadProgress(0);
      setProcessingStatus('Preparing to insert pages...');

      await apiRequest('POST', `/api/documents/${documentId}/pages/insert`, {
        cacheKey,
        selectedPages: Array.from(selectedPages),
        insertionPosition: 'end',
        versionId,
        showNewBadge: true
      });

      const pollStartTime = Date.now();
      const pollTimeout = 60000;

      const pollInterval = setInterval(async () => {
        try {
          if (Date.now() - pollStartTime > pollTimeout) {
            clearInterval(pollInterval);
            setStep('select');
            toast({
              title: "Processing timeout",
              description: "Processing is taking longer than expected. Please refresh the page.",
              variant: "destructive"
            });
            return;
          }

          const statusResponse = await fetch(`/api/documents/versions/${versionId}/status`);
          const status = await statusResponse.json();
          
          setUploadProgress(status.progress);
          setProcessingStatus(getStatusText(status.status, status.progress, status.currentPage, status.pageCount));
          
          if (status.status === "completed") {
            clearInterval(pollInterval);
            toast({
              title: "Success!",
              description: `Added ${selectedPages.size} page${selectedPages.size > 1 ? 's' : ''} to the document.`
            });
            
            const insertedPageIds = status.insertedPageIds || [];
            onProcessingComplete?.(insertedPageIds);
            handleClose();
          } else if (status.status === "failed") {
            clearInterval(pollInterval);
            setStep('select');
            toast({
              title: "Processing failed",
              description: "Failed to insert pages. Please try again.",
              variant: "destructive"
            });
          }
        } catch (error) {
          clearInterval(pollInterval);
          setStep('select');
          console.error("Failed to poll status:", error);
          toast({
            title: "Error",
            description: "Failed to check processing status.",
            variant: "destructive"
          });
        }
      }, 2000);
    } catch (error: any) {
      console.error('Failed to insert pages:', error);
      setStep('select');
      toast({
        title: "Failed to insert pages",
        description: error.message || "Please try again.",
        variant: "destructive"
      });
    }
  };

  const renderChoiceStep = () => (
    <div className="space-y-4 px-6">
      <div className="text-center mb-6">
        <h3 className="text-lg font-semibold mb-2">How would you like to upload?</h3>
        <p className="text-sm text-muted-foreground">Choose whether to create a new version or add pages to the current version</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card 
          className="p-6 cursor-pointer transition-colors border-2 hover:border-opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            borderColor: 'hsl(var(--border))',
            ['--hover-border-color' as any]: colorToRgba(documentColor, 0.5),
            ['--focus-ring-color' as any]: documentColor
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--hover-border-color)'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'hsl(var(--border))'}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--hover-border-color)';
            e.currentTarget.style.setProperty('box-shadow', `0 0 0 2px var(--focus-ring-color, ${documentColor})`);
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'hsl(var(--border))';
            e.currentTarget.style.removeProperty('box-shadow');
          }}
          onClick={() => {
            handleClose();
            // Trigger normal upload
            window.location.href = `/doc/${documentId}`;
          }}
          tabIndex={0}
          data-testid="button-upload-new-version"
        >
          <div className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorToRgba(documentColor, 0.12) }}>
              <FilePlus className="w-8 h-8" style={{ color: documentColor }} />
            </div>
            <h4 className="font-semibold mb-2">Upload New Version</h4>
            <p className="text-sm text-muted-foreground">
              Create a complete new version with all pages from the PDF
            </p>
          </div>
        </Card>

        <Card 
          className="p-6 cursor-pointer transition-colors border-2 hover:border-opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{
            borderColor: 'hsl(var(--border))',
            ['--hover-border-color' as any]: colorToRgba(documentColor, 0.5),
            ['--focus-ring-color' as any]: documentColor
          }}
          onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--hover-border-color)'}
          onMouseLeave={(e) => e.currentTarget.style.borderColor = 'hsl(var(--border))'}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = 'var(--hover-border-color)';
            e.currentTarget.style.setProperty('box-shadow', `0 0 0 2px var(--focus-ring-color, ${documentColor})`);
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = 'hsl(var(--border))';
            e.currentTarget.style.removeProperty('box-shadow');
          }}
          onClick={() => fileInputRef.current?.click()}
          tabIndex={0}
          data-testid="button-add-pages"
        >
          <div className="text-center">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: colorToRgba(documentColor, 0.12) }}>
              <Plus className="w-8 h-8" style={{ color: documentColor }} />
            </div>
            <h4 className="font-semibold mb-2">Add Pages</h4>
            <p className="text-sm text-muted-foreground">
              Select specific pages from a PDF to insert into the current version
            </p>
          </div>
        </Card>
      </div>
    </div>
  );

  const renderUploadStep = () => {
    // Only show processing UI if mutation is actually running
    if (!uploadThumbnailMutation.isPending) {
      return (
        <div className="px-6 text-center">
          <div className="w-12 h-12 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: colorToRgba(documentColor, 0.12) }}>
            <Upload className="w-8 h-8" style={{ color: documentColor }} />
          </div>
          <h3 className="text-lg font-semibold mb-2">Select a PDF File</h3>
          <p className="text-sm text-muted-foreground">Choose a file to add pages from</p>
        </div>
      );
    }

    return (
      <div className="px-6 text-center">
        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: documentColor }} />
        <h3 className="text-lg font-semibold mb-2">Processing PDF...</h3>
        <p className="text-sm text-muted-foreground">Generating page previews</p>
      </div>
    );
  };

  const renderSelectStep = () => {
    const isViewingOldVersion = documentCurrentVersion && versionNumber !== documentCurrentVersion;
    
    return (
      <>
        {isViewingOldVersion && (
          <div className="mx-4 sm:mx-6 my-4 p-3 rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
            <p className="text-sm text-blue-900 dark:text-blue-100">
              <strong>Note:</strong> You're viewing version {versionNumber}, but pages will be added to the latest version (v{documentCurrentVersion}).
            </p>
          </div>
        )}

        <div className="px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 py-4">
            {thumbnails.map((page) => (
              <div
                key={page.pageNumber}
                className="relative border-2 rounded-lg p-2 cursor-pointer transition-all min-h-[48px]"
                style={{
                  borderColor: selectedPages.has(page.pageNumber) 
                    ? documentColor 
                    : colorToRgba(documentColor, 0.2),
                  backgroundColor: selectedPages.has(page.pageNumber)
                    ? colorToRgba(documentColor, 0.05)
                    : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!selectedPages.has(page.pageNumber)) {
                    e.currentTarget.style.borderColor = colorToRgba(documentColor, 0.5);
                  }
                }}
                onMouseLeave={(e) => {
                  if (selectedPages.has(page.pageNumber)) {
                    e.currentTarget.style.borderColor = documentColor;
                  } else {
                    e.currentTarget.style.borderColor = colorToRgba(documentColor, 0.2);
                  }
                }}
                onClick={() => togglePageSelection(page.pageNumber)}
                data-testid={`page-thumbnail-${page.pageNumber}`}
              >
                <img
                  src={page.thumbnailBase64}
                  alt={`Page ${page.pageNumber}`}
                  className="w-full h-auto rounded"
                />
                <div className="text-center mt-1 text-xs font-medium">
                  Page {page.pageNumber}
                </div>
                {selectedPages.has(page.pageNumber) && (
                  <div 
                    className="absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: documentColor }}
                  >
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  const renderProcessingStep = () => (
    <div className="p-4 sm:p-6">
      <div className="text-center mb-4">
        <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" style={{ color: documentColor }} />
        <h3 className="text-lg font-semibold mb-2">Inserting Pages...</h3>
        <p className="text-sm text-muted-foreground">
          {processingStatus || "Processing selected pages at full quality"}
        </p>
      </div>
      
      {uploadProgress > 0 && (
        <div className="mt-4">
          <div className="flex justify-between text-sm mb-2">
            <span className="text-muted-foreground">Progress</span>
            <span className="font-medium">{uploadProgress}%</span>
          </div>
          <Progress 
            value={uploadProgress} 
            className="h-2" 
            style={{
              backgroundColor: colorToRgba(documentColor, 0.1)
            }}
            indicatorStyle={{
              backgroundColor: documentColor
            }}
          />
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-hidden rounded-xl p-0 flex flex-col gap-0">
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="flex items-center">
            <Upload className="w-5 h-5 mr-2" style={{ color: documentColor }} />
            {step === 'select' ? 'Select Pages' : 'Upload Document'}
          </DialogTitle>
        </DialogHeader>

        {/* Hidden file input - always available */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileSelect(file);
          }}
          className="hidden"
        />

        <div className="flex-1 overflow-y-auto">
          {step === 'choice' && renderChoiceStep()}
          {step === 'upload' && renderUploadStep()}
          {step === 'select' && renderSelectStep()}
          {step === 'processing' && renderProcessingStep()}
        </div>

        {step === 'select' && (
          <div className="bg-background border-t px-4 sm:px-6 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
            <div className="flex flex-row items-center justify-between gap-3 pt-3 sm:pt-4">
              <Button
                variant="outline"
                onClick={() => setStep('choice')}
                className="flex-1 sm:flex-initial sm:w-auto h-12 sm:h-10"
                data-testid="button-back-to-choice"
              >
                Back
              </Button>
              <Button
                onClick={handleConfirm}
                disabled={selectedPages.size === 0}
                className="flex-1 sm:flex-initial sm:w-auto h-12 sm:h-10"
                style={{ backgroundColor: documentColor }}
                data-testid="button-insert-pages"
              >
                Insert Pages
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
